import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiPlayCircle, FiStar } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

// Bottom-sheet shown on iOS when a free user goes past their free allowance
// for a gated action. Opt-in: watch a rewarded ad to continue, or go Pro to
// remove ads. Dismissing aborts the action. Mirrors PaywallModal styling.
const GATE_COPY = {
  smart_schedule: {
    title: 'Out of free runs this month',
    body: 'You’ve used your free Smart Schedule run for this month. Watch a short ad to run it again, or go Pro for an ad-free experience.',
  },
  planned_round: {
    title: 'One upcoming round at a time',
    body: 'You already have an upcoming round on the free plan. Watch a short ad to plan another, or go Pro for an ad-free experience.',
  },
};

const DEFAULT_COPY = {
  title: 'Watch an ad to continue',
  body: 'Watch a short ad to continue, or go Pro for an ad-free experience.',
};

function RewardedGateSheet({ isOpen, feature, busy, onWatch, onUpgrade, onClose }) {
  if (!isOpen) return null;
  const copy = GATE_COPY[feature] || DEFAULT_COPY;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4 z-[60]"
        onClick={busy ? undefined : onClose}
      >
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="bg-card rounded-t-lg sm:rounded-lg shadow-modal max-w-md w-full mb-20 sm:mb-0"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-5">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-lg bg-accent/10 flex items-center justify-center">
                  <SafeIcon icon={FiPlayCircle} className="w-5 h-5 text-accent" />
                </div>
                <h2 className="text-lg font-bold text-text-primary">{copy.title}</h2>
              </div>
              <button
                type="button"
                onClick={onClose}
                disabled={busy}
                aria-label="Close"
                className="w-8 h-8 bg-surface rounded-lg flex items-center justify-center hover:bg-border-subtle transition-colors disabled:opacity-50"
              >
                <SafeIcon icon={FiX} className="w-4 h-4 text-text-secondary" />
              </button>
            </div>

            <p className="text-sm text-text-secondary mb-5">{copy.body}</p>

            <div className="space-y-2">
              <button
                type="button"
                onClick={onWatch}
                disabled={busy}
                className="w-full px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors inline-flex items-center justify-center gap-2 bg-accent text-white hover:bg-interactive-hover disabled:opacity-50"
              >
                <SafeIcon icon={FiPlayCircle} className="w-4 h-4" />
                {busy ? 'Loading ad…' : 'Watch a short ad'}
              </button>
              <button
                type="button"
                onClick={onUpgrade}
                disabled={busy}
                className="w-full px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors inline-flex items-center justify-center gap-2 border border-accent text-accent hover:bg-accent/5 disabled:opacity-50"
              >
                <SafeIcon icon={FiStar} className="w-4 h-4" />
                Go Pro - remove ads
              </button>
              <button
                type="button"
                onClick={onClose}
                disabled={busy}
                className="w-full px-4 py-2.5 rounded-lg text-sm font-semibold text-text-secondary hover:bg-surface transition-colors disabled:opacity-50"
              >
                Not now
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default RewardedGateSheet;
